import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { brand } from "../../../config/theme";
import { Button, Card, EmptyState, Table, notifyError, notifyInfo, type TableColumn } from "../../../components/ui";
import { SectionFrame } from "../components/SectionFrame";
import { useRadarMutation, useSnapshot } from "../hooks/useRadarCorreo";
import { radarCorreoApi, apiErrorMessage, type CounterpartyMetrics, type Snapshot } from "../services/radarCorreoService";
import { fmtDate, fmtHours, fmtMoneyMap, fmtPct } from "../lib/format";

// Gráficas de una sola serie (sin leyenda: el título la nombra), un tono, barras
// delgadas con extremo redondeado, cuadrícula discreta y tooltip. El detalle
// completo vive en la tabla (vista accesible de los mismos datos).

const RANKINGS = ["bestCarriers", "mostBusiness", "mostValue", "fastestResponse", "slowestResponse", "mostProblems", "mostOverdue"] as const;

const tooltipStyle = { background: "rgb(var(--c-card))", border: "1px solid rgb(var(--c-border))", borderRadius: 8, fontSize: 12, color: "rgb(var(--c-content))" };
const axisTick = { fontSize: 11, fill: "rgb(var(--c-content-muted))" };

function Kpi({ label, value, tone }: { label: string; value: string | number; tone?: "error" | "warning" }) {
  return (
    <Card padding="sm">
      <p className="text-xs text-content-secondary">{label}</p>
      <p className={`mt-1 text-lg font-bold tabular-nums ${tone === "error" ? "text-error" : tone === "warning" ? "text-warning" : "text-content"}`}>{value}</p>
    </Card>
  );
}

function rankingValue(id: (typeof RANKINGS)[number], c: CounterpartyMetrics, lang: string): string {
  switch (id) {
    case "bestCarriers":
      return `${c.score}`;
    case "mostBusiness":
      return `${c.operations}`;
    case "mostValue":
      return fmtMoneyMap(c.value, lang);
    case "fastestResponse":
    case "slowestResponse":
      return fmtHours(c.responseMedianHours);
    case "mostProblems":
      return `${c.delays + c.incidents + c.customsHolds}`;
    case "mostOverdue":
      return fmtMoneyMap(c.overdue, lang);
  }
}

function Rankings({ snap }: { snap: Snapshot }) {
  const { t, i18n } = useTranslation("radarCorreo");
  const byId = new Map(snap.metrics.counterparties.map((c) => [c.counterpartyId, c]));
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {RANKINGS.map((id) => {
        const list = (snap.metrics.rankings[id] || []).map((cid) => byId.get(cid)).filter(Boolean).slice(0, 5) as CounterpartyMetrics[];
        if (!list.length) return null;
        return (
          <Card key={id} padding="sm" className="space-y-2">
            <h3 className="text-sm font-semibold text-content">{t(`snapshot.rankings.${id}`)}</h3>
            <ol className="space-y-1.5">
              {list.map((c, i) => (
                <li key={c.counterpartyId} className="flex items-center gap-2 text-sm">
                  <span className="w-4 text-xs tabular-nums text-content-faint">{i + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-content">{c.name}</span>
                  <span className="tabular-nums text-content-secondary">{rankingValue(id, c, i18n.language)}</span>
                </li>
              ))}
            </ol>
          </Card>
        );
      })}
    </div>
  );
}

export default function SnapshotSection() {
  const { t, i18n } = useTranslation("radarCorreo");
  const lang = i18n.language?.startsWith("en") ? "en" : "es";
  const snap = useSnapshot();
  const regenerate = useRadarMutation(() => radarCorreoApi.regenerateSnapshot());
  const s = snap.data;

  const columns = useMemo<TableColumn<CounterpartyMetrics>[]>(
    () => [
      { key: "name", header: t("snapshot.table.name"), render: (c) => <span className="font-medium text-content">{c.name}</span> },
      { key: "score", header: t("snapshot.table.score"), align: "right", render: (c) => c.score },
      { key: "ops", header: t("snapshot.table.operations"), align: "right", render: (c) => c.operations },
      { key: "value", header: t("snapshot.table.value"), align: "right", hideOnMobile: true, render: (c) => fmtMoneyMap(c.value, i18n.language) },
      { key: "onTime", header: t("snapshot.table.onTime"), align: "right", render: (c) => fmtPct(c.onTimeRate) },
      { key: "resp", header: t("snapshot.table.response"), align: "right", render: (c) => fmtHours(c.responseMedianHours) },
      { key: "prob", header: t("snapshot.table.problems"), align: "right", render: (c) => `${c.delays} / ${c.incidents + c.customsHolds}` },
      { key: "win", header: t("snapshot.table.winRate"), align: "right", hideOnMobile: true, render: (c) => fmtPct(c.quoteWinRate) },
      { key: "pay", header: t("snapshot.table.paymentDays"), align: "right", hideOnMobile: true, render: (c) => (c.avgPaymentDays == null ? "—" : t("snapshot.days", { value: c.avgPaymentDays })) },
      { key: "over", header: t("snapshot.table.overdue"), align: "right", hideOnMobile: true, render: (c) => fmtMoneyMap(c.overdue, i18n.language) },
    ],
    [t, i18n.language],
  );

  const actions = (
    <Button
      variant="secondary"
      icon={<i className="ri-refresh-line" />}
      loading={regenerate.isPending}
      onClick={() =>
        regenerate.mutate(undefined, {
          onSuccess: () => void notifyInfo(t("snapshot.generating")),
          onError: (err) => void notifyError(t("common.error"), apiErrorMessage(err) || undefined),
        })
      }
    >
      {t("snapshot.generate")}
    </Button>
  );

  return (
    <SectionFrame title={t("snapshot.title")} subtitle={t("snapshot.subtitle")} actions={actions} loading={snap.isLoading} error={snap.error} onRetry={() => void snap.refetch()}>
      {!s ? (
        <EmptyState icon="ri-camera-lens-line" title={t("snapshot.empty")} />
      ) : (
        <div className="space-y-5" data-testid="radar-snapshot">
          <p className="text-xs text-content-secondary">{t("snapshot.generatedAt", { date: fmtDate(s.generatedAt, i18n.language), days: s.periodDays })}</p>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Kpi label={t("snapshot.kpis.operations")} value={s.metrics.totals.operations} />
            <Kpi label={t("snapshot.kpis.counterparties")} value={s.metrics.totals.counterparties} />
            <Kpi label={t("snapshot.kpis.value")} value={fmtMoneyMap(s.metrics.totals.value, i18n.language)} />
            <Kpi label={t("snapshot.kpis.receivableOverdue")} value={fmtMoneyMap(s.metrics.totals.receivableOverdue, i18n.language)} tone={Object.keys(s.metrics.totals.receivableOverdue || {}).length ? "error" : undefined} />
            <Kpi label={t("snapshot.kpis.payableOverdue")} value={fmtMoneyMap(s.metrics.totals.payableOverdue, i18n.language)} tone={Object.keys(s.metrics.totals.payableOverdue || {}).length ? "warning" : undefined} />
            <Kpi label={t("snapshot.kpis.problems")} value={`${s.metrics.totals.delays} / ${s.metrics.totals.incidents}`} />
          </div>

          {s.narrative ? (
            <Card className="space-y-3">
              <h3 className="text-sm font-semibold text-content">{t("snapshot.narrative")}</h3>
              <p className="text-sm text-content">{s.narrative[lang].summary}</p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {(["findings", "risks", "recommendations"] as const).map((k) =>
                  s.narrative![lang][k].length ? (
                    <div key={k}>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-content-secondary">{t(`snapshot.${k}`)}</p>
                      <ul className="list-disc space-y-1 pl-4 text-sm text-content">
                        {s.narrative![lang][k].map((x) => (
                          <li key={x}>{x}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null,
                )}
              </div>
            </Card>
          ) : s.narrativeError ? (
            <p className="text-xs text-content-secondary">{t("snapshot.noNarrative", { reason: s.narrativeError })}</p>
          ) : null}

          <Rankings snap={s} />

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {s.metrics.trend.length > 0 && (
              <Card padding="sm">
                <h3 className="mb-2 text-sm font-semibold text-content">{t("snapshot.trend")}</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={s.metrics.trend} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke="rgb(var(--c-border) / 0.5)" />
                      <XAxis dataKey="month" tick={axisTick} tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tick={axisTick} tickLine={false} axisLine={false} />
                      <Tooltip cursor={{ fill: "rgb(var(--c-subtle))" }} contentStyle={tooltipStyle} formatter={(v) => [v, t("snapshot.kpis.operations")]} />
                      <Bar dataKey="operations" fill={brand.primary} radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}
            {Object.keys(s.metrics.modes).length > 0 && (
              <Card padding="sm">
                <h3 className="mb-2 text-sm font-semibold text-content">{t("snapshot.modes")}</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={Object.entries(s.metrics.modes).map(([mode, n]) => ({ mode: t(`operations.mode.${mode}`, { defaultValue: mode }), n })).sort((a, b) => b.n - a.n)}
                      margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
                    >
                      <CartesianGrid horizontal={false} stroke="rgb(var(--c-border) / 0.5)" />
                      <XAxis type="number" allowDecimals={false} tick={axisTick} tickLine={false} axisLine={false} />
                      <YAxis type="category" dataKey="mode" width={90} tick={axisTick} tickLine={false} axisLine={false} />
                      <Tooltip cursor={{ fill: "rgb(var(--c-subtle))" }} contentStyle={tooltipStyle} formatter={(v) => [v, t("snapshot.kpis.operations")]} />
                      <Bar dataKey="n" fill={brand.primary} radius={[0, 4, 4, 0]} maxBarSize={18} isAnimationActive={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}
          </div>

          {s.metrics.lanes.length > 0 && (
            <Card padding="sm">
              <h3 className="mb-2 text-sm font-semibold text-content">{t("snapshot.lanes")}</h3>
              <ul className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
                {s.metrics.lanes.map((l) => (
                  <li key={`${l.origin}>${l.destination}`} className="flex justify-between gap-2">
                    <span className="truncate text-content">
                      {l.origin} → {l.destination}
                    </span>
                    <span className="tabular-nums text-content-secondary">{l.operations}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card padding="none">
            <div className="flex items-center justify-between gap-2 p-3">
              <h3 className="text-sm font-semibold text-content">{t("snapshot.table.title")}</h3>
            </div>
            <Table columns={columns} rows={s.metrics.counterparties} rowKey={(c) => c.counterpartyId} compact mobileCards />
          </Card>
        </div>
      )}
    </SectionFrame>
  );
}
